import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocale } from '@wovendays/i18n';
import type { Locale } from '@wovendays/i18n';
import type { AuthUser } from '@wovendays/api-client';
import { apiClient } from '@/lib/api-client';

/**
 * Syncs locale preference with the server.
 * - On login: applies the user's saved locale from the session response
 * - persistLocale: saves locale preference to the database
 */
export function useLocaleSync(user: AuthUser | null) {
  const { setLocale } = useLocale();
  const appliedRef = useRef(false);

  // On login: apply user's saved locale (once)
  useEffect(() => {
    if (user?.locale && !appliedRef.current) {
      setLocale(user.locale as Locale);
      appliedRef.current = true;
    }
    if (!user) {
      appliedRef.current = false;
    }
  }, [user, setLocale]);

  const mutation = useMutation({
    mutationFn: (locale: Locale) =>
      apiClient.put('/api/auth/profile', { locale }),
  });

  const persistLocale = (locale: Locale) => {
    mutation.mutate(locale);
  };

  return { persistLocale };
}
